import { Children } from "react";

import { Box, Divider, Typography, useTheme } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Label } from "recharts";
import { CardCore } from "src/Pages/components/CardCore/CardCore";
import { CardBodyCore } from "src/Pages/components/CardCore/Components/CardBodyCore";
import { FormatMoney } from "src/shared/Utils/FormatMoney";

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      name: string;
      total_producao?: number;
      percentProducao: string;
      total_contratos: string;
      total_vidas: number;
    };
  }>;
  labelStyle?: React.CSSProperties;
}

const CustomTooltip: React.FC<TooltipProps> = ({
  active,
  payload,
}) => {
  if (!active || !payload || payload.length === 0) { return null; }

  const data = payload[0].payload;

  const name = data.name;
  const value = data.total_producao;
  const total_contratos = data.total_contratos;
  const total_vidas = data.total_vidas;

  return (
    <CardCore>
      <Typography style={{ fontSize: "1rem", fontWeight: "bold" }}>
        {name}
      </Typography>
      <Typography style={{ fontSize: "0.9rem", color: "text.secondary" }}>
        {`${total_contratos} contratos - ${total_vidas} vidas`}
      </Typography>

      {Number(total_contratos) > 0 && (
        <Typography style={{ fontSize: "0.9rem", color: "text.secondary" }}>
          {`R$ ${FormatMoney((value ?? 0).toFixed(2))} (${data.percentProducao}%)`}
        </Typography>
      )}
    </CardCore>
  );
};

interface IGraphicPieCore {
  title: string;
  data: {
    name: string;
    total_producao: string | null;
    percentProducao: string;
    total_contratos: string;
    total_vidas: number;
  }[];
}

export const GraphicCore = (
  { title, data }: IGraphicPieCore
) => {
  const theme = useTheme();

  const COLORS = [
    theme.palette.primary.main,
    "#29BD3DFF", "#FA5D60FF", "#2977D0FF", "#E35BF6FF", "#F8BD4DFF",
    "#FF9A00FF", "#36D56BFF", "#8066EBFF", "#DD4BF0FF", "#1291BFFF",
    "#FF6F1EFF", "#42D7E2FF", "#B84DE8FF", "#8DD43BFF", "#FF4F93FF",
    "#2E9BFFFF", "#F4C63DFF", "#6E49DFFF", "#48D7A7FF", "#F967B2FF",
    "#2DC16BFF", "#FF8E3CFF", "#53B2FFFF", "#C949E6FF", "#FFD23BFF",
    "#35A3EFFF", "#E34F4FFF", "#7FD83CFF", "#B35CFFFF", "#FF7A3FFF"
  ];

  const pieData =
    Number(data[0]?.total_contratos) > 0
      ? data.map((item) => ({
        name: item.name,
        total_producao: Number(item.total_producao),
        percentProducao: item.percentProducao,
        total_contratos: item.total_contratos,
        total_vidas: item.total_vidas,
      }))
      : [
        {
          name: 'Nenhum contrato',
          total_producao: 100,
          percentProducao: 0,
          total_contratos: 0,
          total_vidas: 0,
        },
      ]


  return (
    <CardCore sxStyle={{ margin: "0", height: "100%" }}>
      <CardBodyCore title={title}>

        <ResponsiveContainer height={170}>
          <PieChart style={{ padding: 0, margin: 0 }}>
            <Tooltip content={<CustomTooltip />} />
            <Pie
              data={pieData}
              dataKey="total_producao"


              innerRadius="80%"
              outerRadius="100%"
              // Corner radius is the rounded edge of each pie slice
              cornerRadius="30%"
              fill="#8884d8"
              // padding angle is the gap between each pie slice
              paddingAngle={2}
            >
              <>
                <Label position="center" fill="#666">
                  Produção
                </Label>
                {data.map((item, index) => (
                  <Cell key={`cell-${item.name}`} fill={pieData.length === 1 ? '#A8C3EBFF' : COLORS[index % COLORS.length]}
                  />
                ))}
              </>
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <Divider sx={{ margin: "16px 0", borderColor: "#EBEAEA80" }} />

        <Box sx={{ maxHeight: "180px", overflowY: "auto", paddingRight: "8px" }}>
          {Children.toArray(
            data.map((item, index) => (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "2px" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: "7px",
                      height: "7px",
                      marginRight: "5px",
                      borderRadius: "50%",
                      background: COLORS[index % COLORS.length],
                    }}
                  />
                  <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                    {item.name}:
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                  {FormatMoney(item.total_producao?.toString() ?? "0,00")}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </CardBodyCore>
    </CardCore>
  );
};
